import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import GoogleMapReact from 'google-map-react';
import Supercluster from 'supercluster';
import { get, isNumber, isString, isNil, isArray, map as _map, result, size } from 'lodash';
import ReactGoogleMapLoader from 'react-google-maps-loader';
import sha256 from 'node-forge/lib/sha256';
import SimpleMarker from './Markers/SimpleMarker';
import ClusterMarker from './Markers/ClusterMarker';
import ClusterManyMarkers from './Markers/ClusterManyMarkers';
import { isMobile } from '../../../common/services/isMobile';
import { chargerStateLabels } from '../../../common/constants/CommonUsedDropdownsStatics';
import { GOOGLE_MAPS_API_TOKEN } from '../../../config';
import { MAP_MODE_HEATMAP, MAP_MODE_REGULAR } from '../component';
import { getFilterStarted } from '../selectors';
import uiLabelsPaths from '../../../common/services/uiFieldLabelsDictionary';

const newYorkCoordinates = { lat: 40.73061, lng: -73.935242 };
const heatMapRadius = 30;
const detailedClustersZoomLimit = 14;
const maxZoomLimit = 21;

export const gmapKey = GOOGLE_MAPS_API_TOKEN;
export const keys = { key: gmapKey, v: '3.55' };
export const dashboardMapKeys = { ...keys, libraries: 'visualization,places' };

const checkIfLatLngValid = (lat, lng) => isNumber(lng) && isNumber(lat) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;

export const defaultGmapProps = {
  center: { lat: 40.73061, lng: -73.935242 },
  zoom: 11,
};

const getClusters = (markers, minZoom, radius, zoom, pointsLimit = Infinity) => {
  const index = new Supercluster({
    minZoom, // min zoom to generate clusters on
    maxZoom: maxZoomLimit,
    radius, // cluster radius in pixels
    extent: 256,
  });

  index.load(markers);

  return () =>
    markers.length > 0
      ? index.getClusters([-180, -85, 180, 85], zoom).map((cluster) => {
          if (get(cluster, 'properties.cluster', null) === true) {
            const data = index.getLeaves(cluster.id, pointsLimit);

            return { ...cluster, data };
          }

          return cluster;
        })
      : [];
};

function GMap({ chargers, config, viewMode, openChangerModal, options, clusterRadius, setFilterFinished }) {
  const [markers, setMarkers] = useState([]);
  const [touchMarkerId, setTouchMarkerId] = useState(null);
  const [mapProps, setMapProps] = useState({
    center: newYorkCoordinates,
    zoom: 10,
  });
  const [zoomProp, setZoomProp] = useState(null);
  const [isMobileMode, setIsMobileMode] = useState(isMobile);
  const [clusters, setClusters] = useState([]);
  const [prevViewMode, setPrevViewMode] = useState(null);
  const [prevChargers, setPrevChargers] = useState([]);

  const map = useRef(null);
  const maps = useRef(null);
  const heatmap = useRef(null);
  const heatMapData = useRef(null);
  const autoFitFunctionAfterMapReady = useRef(null);

  const autoFit = useSelector(getFilterStarted);

  const onChange = useCallback(
    ({ center, zoom, bounds }) => {
      setMapProps({ center, zoom, bounds });
      setZoomProp(zoom);
    },
    [setMapProps, setZoomProp],
  );

  const onChildMouseEnter = (hoverKey, { id: clusterId }) =>
    setClusters(
      clusters.map(({ id, ...cluster }) => ({
        ...cluster,
        id,
        hovered: id === clusterId,
      })),
    );

  const onChildMouseLeave = () =>
    setClusters(
      clusters.map(({ id, ...cluster }) => ({
        ...cluster,
        id,
        hovered: false,
      })),
    );

  const getCluster = useMemo(() => {
    const { minZoom } = options;

    return getClusters(markers, minZoom, clusterRadius, zoomProp);
  }, [zoomProp, markers]);

  const fillHeatMap = () => {
    heatmap.current &&
      heatmap.current.setData(
        heatMapData.current && heatMapData.current.length
          ? heatMapData.current.map(({ location }) => ({
              location: new maps.current.LatLng(location.lat, location.lng),
              weight: 1,
            }))
          : [],
      );
  };

  const fillAutoFitBounds = () => {
    const latlngbounds = new maps.current.LatLngBounds(null);

    for (const latLngObj of heatMapData.current) {
      latlngbounds.extend(latLngObj.location);
    }
    map.current.fitBounds(latlngbounds, -10);
  };

  const finishInitMap = () => {
    fillHeatMap();
    if (heatMapData.current && heatMapData.current.length) {
      fillAutoFitBounds();
    }
  };

  const autoFitMap = () => {
    if (!map.current) {
      autoFitFunctionAfterMapReady.current = () => finishInitMap();
    } else {
      finishInitMap();
    }
    setFilterFinished();
  };

  const toggleNextViewModeIfNeeded = (viewMode) => {
    if (heatmap.current) {
      heatmap.current.setMap(viewMode === MAP_MODE_HEATMAP && map.current ? map.current : null);

      if (viewMode === MAP_MODE_HEATMAP) {
        fillHeatMap();
      }
    }
  };

  useEffect(() => {
    if (chargers && chargers !== prevChargers) {
      heatMapData.current = []; // not only heatmap, just all markers with google lat lng
      const productName = result(config, 'product', '').toUpperCase() || 'Product';

      const markers = chargers
        .map((charger) => {
          const chargerStateLabel = chargerStateLabels[get(charger, 'charger_state')] || '';
          const chargerState = get(charger, 'c_online') ? chargerStateLabel : 'Unknown';
          const lat = get(charger, 'c_latitude');
          const lng = get(charger, 'c_longitude');
          const id = get(charger, 'c_uuid');
          const cStatus = get(charger, 'c_online') ? get(charger, 'c_status') : 'unknown';
          const online = get(charger, 'c_online') || false ? 'Yes' : 'No';
          const productType = result(charger, uiLabelsPaths.p_type, '');
          const condition = get(charger, 'c_condition') || '';
          const stateProcessed = isString(cStatus) ? cStatus.toLowerCase() : null;
          const name = get(charger, 'c_name') || '';
          const timestamp = get(charger, 'c_status_ts') || '';
          const productVersion = get(charger, 'c_product_version') || '';
          const productSerialNumber = get(charger, 'c_product_serial_number') || '';

          if (checkIfLatLngValid(lat, lng) && id) {
            heatMapData.current.push({
              location: { lat, lng },
              weight: 1,
            });

            return {
              id,
              lat,
              lng,
              geometry: {
                coordinates: [lng, lat],
              },
              markerClassName: stateProcessed,

              data: {
                id,
                name,
                timestamp,
                productVersion,
                productSerialNumber,
                productName,
                state: stateProcessed,
                chargerState,
                online,
                productType,
                condition,
              },
            };
          }

          return null;
        })
        .filter((charger) => !isNil(charger));

      setMarkers(markers);

      fillHeatMap();

      if (autoFit) {
        autoFitMap();
      }
    }

    if (viewMode !== prevViewMode) {
      toggleNextViewModeIfNeeded(viewMode);
    }

    setPrevChargers(chargers);
    setPrevViewMode(viewMode);
  }, [chargers, viewMode, autoFit]);

  useEffect(() => {
    setClusters(
      mapProps.bounds
        ? getCluster(mapProps).map((thisProps) => {
            const {
              geometry: { coordinates },
            } = thisProps;
            const [wx, wy] = coordinates;

            if (get(thisProps, 'properties.cluster', null) === true) {
              return {
                // case of cluster with > 1 marker
                lat: wy,
                lng: wx,
                geometry: {
                  coordinates: [wx, wy],
                },
                text: thisProps.properties.point_count,
                numPoints: thisProps.properties.point_count,
                data: thisProps.data,
                id: `${thisProps.properties.point_count}_${thisProps.id}`,
              };
            }

            return {
              lat: wy,
              lng: wx,
              geometry: {
                coordinates: [wx, wy],
              },
              text: 1,
              numPoints: 1,
              data: thisProps.data,
              markerClassName: thisProps.markerClassName,
              id: `${1}_${thisProps.id}`,
            };
          })
        : [],
    );
  }, [mapProps, getCluster]);

  useEffect(() => {
    setIsMobileMode(isMobile);
  }, [isMobile]);

  const onShowMarkers = (markersDataArray) => {
    const latlngbounds = new maps.current.LatLngBounds(null);

    markersDataArray.forEach((marker) => {
      latlngbounds.extend({ lat: marker.lat, lng: marker.lng });
    });
    map.current.fitBounds(latlngbounds, -10);
  };

  const toggleZoom = (enableZoomFlag) => {
    map.current && map.current.setOptions({ scrollwheel: !!enableZoomFlag });
  };

  const onTouchStartMarker = (id, disablePan) => {
    if (map.current) {
      if (disablePan) map.current.setOptions({ gestureHandling: 'none' });
      setTouchMarkerId(id);
    }
  };

  const onMapTouch = (event) => {
    const eventPath = event.nativeEvent.composedPath ? event.nativeEvent.composedPath() : event.nativeEvent.path;
    const ifNotMap =
      size(
        eventPath.filter(
          (path) => path.classList && (path.classList.contains('i2p2-marker') || path.classList.contains('i2p2-marker-content')),
        ),
      ) > 0;

    if (!ifNotMap) {
      map.current.setOptions({ gestureHandling: 'greedy' });
      setTouchMarkerId(null);
    }
  };

  const onGoogleApiLoaded = ({ map: googleMap, maps: googleMaps }) => {
    map.current = googleMap;
    maps.current = googleMaps;
    heatmap.current = new googleMaps.visualization.HeatmapLayer();
    heatmap.current.set('radius', heatMapRadius);
    toggleNextViewModeIfNeeded(viewMode);
    if (autoFitFunctionAfterMapReady.current) autoFitFunctionAfterMapReady.current();
  };

  const onMapContainerMouseEnter = () => {
    if (!isMobileMode && map.current && map.current.scrollwheel !== true) {
      toggleZoom(true);
    }
  };

  const onTouchStart = isMobileMode ? onMapTouch : null;
  const childMouseEnter = isMobileMode ? null : onChildMouseEnter;
  const childMouseLeave = isMobileMode ? null : onChildMouseLeave;

  return (
    <ReactGoogleMapLoader
      params={dashboardMapKeys}
      render={(googleMaps) =>
        googleMaps && (
          <div className="gmap-container" onMouseEnter={onMapContainerMouseEnter} onTouchStart={onTouchStart}>
            <GoogleMapReact
              yesIWantToUseGoogleMapApiInternals
              defaultCenter={defaultGmapProps.center}
              center={defaultGmapProps.center}
              zoom={defaultGmapProps.zoom}
              options={options}
              onChange={onChange}
              onChildMouseEnter={childMouseEnter}
              onChildMouseLeave={childMouseLeave}
              onGoogleApiLoaded={onGoogleApiLoaded}
            >
              {map.current &&
                viewMode === MAP_MODE_REGULAR &&
                clusters.map(({ id, numPoints, ...markerProps }) => {
                  const mobileHover = touchMarkerId === id;
                  const markerIds = isArray(markerProps.data) && _map(markerProps.data, 'id').join(', ');
                  const clusterId = (markerIds && sha256.create().update(markerIds).digest().toHex()) || id;

                  return numPoints === 1 ? (
                    <SimpleMarker
                      id={id}
                      mobileHover={mobileHover}
                      isMobile={isMobileMode}
                      onTouchStart={onTouchStartMarker}
                      config={config}
                      toggleZoom={toggleZoom}
                      key={clusterId}
                      markerClassName={markerProps.markerClassName}
                      numPoints={numPoints}
                      openChangerModal={openChangerModal}
                      {...markerProps}
                    />
                  ) : map.current.zoom && map.current.zoom <= detailedClustersZoomLimit ? (
                    <ClusterMarker
                      id={id}
                      isMobile={isMobileMode}
                      onTouchStart={onTouchStartMarker}
                      onShowMarkers={onShowMarkers}
                      toggleZoom={toggleZoom}
                      key={clusterId}
                      numPoints={numPoints}
                      maxPoints={markers.length}
                      {...markerProps}
                    />
                  ) : (
                    <ClusterManyMarkers
                      id={id}
                      touchMarkerId={touchMarkerId}
                      isMobile={isMobileMode}
                      onTouchStart={onTouchStartMarker}
                      config={config}
                      toggleZoom={toggleZoom}
                      key={clusterId}
                      numPoints={numPoints}
                      openChangerModal={openChangerModal}
                      {...markerProps}
                    />
                  );
                })}
            </GoogleMapReact>
          </div>
        )
      }
    />
  );
}

GMap.defaultProps = {
  clusterRadius: 80,
  options: {
    minZoom: 3,
    maxZoom: maxZoomLimit,
    fullscreenControl: false,
    disableDefaultUI: true,
    zoomControl: true,
    mapTypeId: 'roadmap',
  },
  style: {
    position: 'relative',
    margin: 0,
    padding: 0,
    flex: 1,
    height: '100%',
  },
};

export default GMap;
